import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Icons } from '../common';
import './UserManagement.css';
import { API_BASE } from '../../constants/urls';


const UserManagement = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [pagination, setPagination] = useState({});
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    status: '',
    page: 1,
    per_page: 20
  });

  // Check if current user is Super Admin
  const isSuperAdmin = user?.role === 'SuperAdmin';

  useEffect(() => {
    if (isSuperAdmin) {
      fetchUsers();
    }
  }, [filters, isSuperAdmin]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams(filters).toString();
      console.log('Fetching users from:', `/api/super-admin/users?${queryParams}`);

      const response = await fetch(`${API_BASE}/api/super-admin/users?${queryParams}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      console.log('Response status:', response.status);
      const data = await response.json();
      console.log('Response data:', data);

      if (response.ok && data.success) {
        setUsers(data.users);
        setPagination(data.pagination);
        setError('');
      } else {
        setError(data.error || `Failed to fetch users (${response.status})`);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError(`Network error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (userData) => {
    try {
      const response = await fetch(`${API_BASE}/api/super-admin/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('User created successfully');
        setShowCreateModal(false);
        fetchUsers();
      } else {
        setError(data.error || 'Failed to create user');
      }
    } catch (err) {
      setError('Network error occurred');
    }
  };

  const handleUpdateUser = async (userId, userData) => {
    try {
      const response = await fetch(`${API_BASE}/api/super-admin/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('User updated successfully');
        setShowEditModal(false);
        setSelectedUser(null);
        fetchUsers();
      } else {
        setError(data.error || 'Failed to update user');
      }
    } catch (err) {
      setError('Network error occurred');
    }
  };

  const handleToggleStatus = async (userId) => {
    try {
      const response = await fetch(`${API_BASE}/api/super-admin/users/${userId}/toggle-status`, {
        method: 'POST',
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(data.message);
        fetchUsers();
      } else {
        setError(data.error || 'Failed to toggle user status');
      }
    } catch (err) {
      setError('Network error occurred');
    }
  };

  const handleResetPassword = async (userId, newPassword) => {
    try {
      const response = await fetch(`${API_BASE}/api/super-admin/users/${userId}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ new_password: newPassword }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('Password reset successfully');
      } else {
        setError(data.error || 'Failed to reset password');
      }
    } catch (err) {
      setError('Network error occurred');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filtering
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  if (!isSuperAdmin) {
    return (
      <div className="user-management">
        <div className="access-denied">
          <Icons.LockIcon className="access-denied-icon" />
          <h2>Access Denied</h2>
          <p>Super Admin privileges required to access user management.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div className="user-management-header">
        <div className="header-content">
          <h1>
            <Icons.UsersIcon className="header-icon" />
            User Management
          </h1>
          <p>Manage system users, roles, and permissions</p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setShowCreateModal(true)}
        >
          <Icons.PlusIcon className="btn-icon" />
          Create User
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="alert alert-error">
          <Icons.AlertCircleIcon className="alert-icon" />
          {error}
          <button onClick={() => setError('')} className="alert-close">×</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <Icons.CheckCircleIcon className="alert-icon" />
          {success}
          <button onClick={() => setSuccess('')} className="alert-close">×</button>
        </div>
      )}

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-row">
          <div className="filter-group">
            <label>Search</label>
            <input
              type="text"
              placeholder="Search by username or email..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="filter-input"
            />
          </div>
          <div className="filter-group">
            <label>Role</label>
            <select
              value={filters.role}
              onChange={(e) => handleFilterChange('role', e.target.value)}
              className="filter-select"
            >
              <option value="">All Roles</option>
              <option value="Admin">Admin</option>
              <option value="Coordinator">Coordinator</option>
              <option value="TE">Technical Editor</option>
              <option value="CE">Copy Editor</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="filter-select"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="users-table-container">
        {loading ? (
          <div className="loading-state">
            <Icons.LoaderIcon className="loading-icon spinning" />
            <p>Loading users...</p>
          </div>
        ) : (
          <>
            <table className="users-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <UserRow
                    key={user.id}
                    user={user}
                    onEdit={(user) => {
                      setSelectedUser(user);
                      setShowEditModal(true);
                    }}
                    onToggleStatus={handleToggleStatus}
                    onResetPassword={handleResetPassword}
                  />
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={!pagination.has_prev}
                  className="pagination-btn"
                >
                  <Icons.ChevronLeftIcon />
                  Previous
                </button>
                <span className="pagination-info">
                  Page {pagination.page} of {pagination.pages} ({pagination.total} total)
                </span>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={!pagination.has_next}
                  className="pagination-btn"
                >
                  Next
                  <Icons.ChevronRightIcon />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateUser}
        />
      )}

      {showEditModal && selectedUser && (
        <EditUserModal
          user={selectedUser}
          onClose={() => {
            setShowEditModal(false);
            setSelectedUser(null);
          }}
          onSubmit={(userData) => handleUpdateUser(selectedUser.id, userData)}
        />
      )}
    </div>
  );
};

// User Row Component
const UserRow = ({ user, onEdit, onToggleStatus, onResetPassword }) => {
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  const getRoleColor = (role) => {
    const colors = {
      SuperAdmin: 'role-super-admin',
      Admin: 'role-admin',
      Coordinator: 'role-coordinator',
      TE: 'role-te',
      CE: 'role-ce'
    };
    return colors[role] || 'role-default';
  };

  return (
    <tr className={`user-row ${!user.isActive ? 'user-inactive' : ''}`}>
      <td>{user.id}</td>
      <td>
        <div className="user-info">
          <span className="username">{user.username}</span>
          {user.role === 'SuperAdmin' && (
            <Icons.CrownIcon className="super-admin-icon" title="Super Admin" />
          )}
        </div>
      </td>
      <td>{user.email || 'N/A'}</td>
      <td>
        <span className={`role-badge ${getRoleColor(user.role)}`}>
          {user.role}
        </span>
        {user.role === 'CE' && user.ceLevel && (
          <div className="ce-details-badge">
            Level: {user.ceLevel.replace('_', ' ')}
            {user.dailyCapacityPages > 0 && ` | Cap: ${user.dailyCapacityPages}p`}
            {user.isBackup && <span className="backup-indicator"> (Backup)</span>}
          </div>
        )}
      </td>
      <td>
        <span className={`status-badge ${user.isActive ? 'status-active' : 'status-inactive'}`}>
          {user.isActive ? 'Active' : 'Inactive'}
        </span>
      </td>
      <td>{formatDate(user.createdAt)}</td>
      <td>{formatDate(user.lastLogin)}</td>
      <td>
        <div className="action-buttons">
          <button
            onClick={() => onEdit(user)}
            className="action-btn edit-btn"
            title="Edit User"
          >
            <Icons.EditIcon />
          </button>
          {user.role !== 'SuperAdmin' && (
            <button
              onClick={() => onToggleStatus(user.id)}
              className={`action-btn ${user.isActive ? 'deactivate-btn' : 'activate-btn'}`}
              title={user.isActive ? 'Deactivate User' : 'Activate User'}
            >
              {user.isActive ? <Icons.UserXIcon /> : <Icons.UserCheckIcon />}
            </button>
          )}
          <button
            onClick={() => setShowPasswordModal(true)}
            className="action-btn password-btn"
            title="Reset Password"
          >
            <Icons.KeyIcon />
          </button>
        </div>

        {showPasswordModal && (
          <PasswordResetModal
            user={user}
            onClose={() => setShowPasswordModal(false)}
            onSubmit={(newPassword) => {
              onResetPassword(user.id, newPassword);
              setShowPasswordModal(false);
            }}
          />
        )}
      </td>
    </tr>
  );
};

// Create User Modal Component
const CreateUserModal = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'CE', // Defaulting to CE as per recent focus
    is_active: true,
    ce_level: 'non_critical',
    daily_capacity_pages: 20,
    is_backup: false
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    try {
      await onSubmit({
        username: formData.username.trim(),
        email: formData.email.trim(),
        password: formData.password,
        role: formData.role,
        is_active: formData.is_active,
        ce_level: formData.role === 'CE' ? formData.ce_level : null,
        daily_capacity_pages: formData.role === 'CE' ? parseInt(formData.daily_capacity_pages) : 0,
        is_backup: formData.role === 'CE' ? formData.is_backup : false
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <Icons.UserPlusIcon className="modal-icon" />
            Create New User
          </h2>
          <button onClick={onClose} className="modal-close">
            <Icons.XIcon />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="username">Username *</label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                className={errors.username ? 'error' : ''}
                placeholder="Enter username"
              />
              {errors.username && <span className="error-text">{errors.username}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={errors.email ? 'error' : ''}
                placeholder="Enter email address"
              />
              {errors.email && <span className="error-text">{errors.email}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="password">Password *</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={errors.password ? 'error' : ''}
                placeholder="Enter password"
              />
              {errors.password && <span className="error-text">{errors.password}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password *</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className={errors.confirmPassword ? 'error' : ''}
                placeholder="Confirm password"
              />
              {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="role">Role *</label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                className={errors.role ? 'error' : ''}
              >
                <option value="Admin">Admin</option>
                <option value="Coordinator">Coordinator</option>
                <option value="TE">Technical Editor</option>
                <option value="CE">Copy Editor</option>
              </select>
              {errors.role && <span className="error-text">{errors.role}</span>}
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                />
                <span className="checkbox-text">Active User</span>
              </label>
            </div>
          </div>

          {formData.role === 'CE' && (
            <div className="ce-specific-fields">
              <h3 className="section-title">CE Smart Batching Details</h3>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="ce_level">CE Level *</label>
                  <select
                    id="ce_level"
                    name="ce_level"
                    value={formData.ce_level}
                    onChange={handleChange}
                  >
                    <option value="non_critical">Non-Critical</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="critical">Critical</option>
                    <option value="premium">Premium</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="daily_capacity_pages">Daily Capacity (Pages) *</label>
                  <input
                    type="number"
                    id="daily_capacity_pages"
                    name="daily_capacity_pages"
                    value={formData.daily_capacity_pages}
                    onChange={handleChange}
                    min="0"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="is_backup"
                      checked={formData.is_backup}
                      onChange={handleChange}
                    />
                    <span className="checkbox-text">Is Backup CE</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="btn btn-primary">
              {loading ? (
                <>
                  <Icons.LoaderIcon className="btn-icon spinning" />
                  Creating...
                </>
              ) : (
                <>
                  <Icons.UserPlusIcon className="btn-icon" />
                  Create User
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit User Modal Component
const EditUserModal = ({ user, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    username: user.username || '',
    email: user.email || '',
    role: user.role || 'TE',
    is_active: user.isActive !== undefined ? user.isActive : true,
    ce_level: user.ceLevel || 'non_critical',
    daily_capacity_pages: user.dailyCapacityPages || 0,
    is_backup: user.isBackup || false,
    current_articles: user.currentArticles || 0
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    try {
      await onSubmit({
        username: formData.username.trim(),
        email: formData.email.trim(),
        role: formData.role,
        is_active: formData.is_active,
        ce_level: formData.role === 'CE' ? formData.ce_level : null,
        daily_capacity_pages: formData.role === 'CE' ? parseInt(formData.daily_capacity_pages) : 0,
        is_backup: formData.role === 'CE' ? formData.is_backup : false,
        current_articles: formData.role === 'CE' ? parseInt(formData.current_articles) : 0
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <Icons.EditIcon className="modal-icon" />
            Edit User: {user.username}
          </h2>
          <button onClick={onClose} className="modal-close">
            <Icons.XIcon />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="username">Username *</label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                className={errors.username ? 'error' : ''}
                placeholder="Enter username"
                disabled={user.role === 'SuperAdmin'}
              />
              {errors.username && <span className="error-text">{errors.username}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={errors.email ? 'error' : ''}
                placeholder="Enter email address"
              />
              {errors.email && <span className="error-text">{errors.email}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="role">Role *</label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                className={errors.role ? 'error' : ''}
                disabled={user.role === 'SuperAdmin'}
              >
                <option value="Admin">Admin</option>
                <option value="Coordinator">Coordinator</option>
                <option value="TE">Technical Editor</option>
                <option value="CE">Copy Editor</option>
              </select>
              {errors.role && <span className="error-text">{errors.role}</span>}
              {user.role === 'SuperAdmin' && (
                <small className="form-help">Super Admin role cannot be changed</small>
              )}
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  disabled={user.role === 'SuperAdmin'}
                />
                <span className="checkbox-text">Active User</span>
              </label>
              {user.role === 'SuperAdmin' && (
                <small className="form-help">Super Admin cannot be deactivated</small>
              )}
            </div>
          </div>

          {formData.role === 'CE' && (
            <div className="ce-specific-fields">
              <h3 className="section-title">CE Smart Batching Details</h3>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="ce_level">CE Level *</label>
                  <select
                    id="ce_level"
                    name="ce_level"
                    value={formData.ce_level}
                    onChange={handleChange}
                  >
                    <option value="non_critical">Non-Critical</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="critical">Critical</option>
                    <option value="premium">Premium</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="daily_capacity_pages">Daily Capacity (Pages) *</label>
                  <input
                    type="number"
                    id="daily_capacity_pages"
                    name="daily_capacity_pages"
                    value={formData.daily_capacity_pages}
                    onChange={handleChange}
                    min="0"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="current_articles">Current Articles Load</label>
                  <input
                    type="number"
                    id="current_articles"
                    name="current_articles"
                    value={formData.current_articles}
                    onChange={handleChange}
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="is_backup"
                      checked={formData.is_backup}
                      onChange={handleChange}
                    />
                    <span className="checkbox-text">Is Backup CE</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="btn btn-primary">
              {loading ? (
                <>
                  <Icons.LoaderIcon className="btn-icon spinning" />
                  Updating...
                </>
              ) : (
                <>
                  <Icons.SaveIcon className="btn-icon" />
                  Update User
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Password Reset Modal Component
const PasswordResetModal = ({ user, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = 'Password must be at least 6 characters';
    }

    if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    try {
      await onSubmit(formData.newPassword);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <Icons.KeyIcon className="modal-icon" />
            Reset Password
          </h2>
          <button onClick={onClose} className="modal-close">
            <Icons.XIcon />
          </button>
        </div>

        <div className="modal-body">
          <p>Reset password for user: <strong>{user.username}</strong></p>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label htmlFor="newPassword">New Password *</label>
            <input
              type="password"
              id="newPassword"
              name="newPassword"
              value={formData.newPassword}
              onChange={handleChange}
              className={errors.newPassword ? 'error' : ''}
              placeholder="Enter new password"
            />
            {errors.newPassword && <span className="error-text">{errors.newPassword}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password *</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              className={errors.confirmPassword ? 'error' : ''}
              placeholder="Confirm new password"
            />
            {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="btn btn-danger">
              {loading ? (
                <>
                  <Icons.LoaderIcon className="btn-icon spinning" />
                  Resetting...
                </>
              ) : (
                <>
                  <Icons.KeyIcon className="btn-icon" />
                  Reset Password
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserManagement;
